import { useMakeRequest, useMutativeRequest } from '../hooks/api'
import { routes } from './constants'
import { useState } from 'react'
import { sanitizeUrlParams } from '../utils/common-helpers'
import type {
  ProfileDetailsResponse,
  SellerMgmtServErrorResponse,
  UnformattedProfileDataResponse,
} from '@/types'
/**
 * This function calls for profile data from given handle
 */
export function useProfileData(handle: string) {
  const [safeHandle] = sanitizeUrlParams(handle)
  const url = `${routes.PROFILE_DATA}/${safeHandle}`
  return useMakeRequest<UnformattedProfileDataResponse>(url)
}

/**
 * Subscribe or unsubscribe to a merchant
 */
export function useSubscriptionHandle(
  handle: string,
  initial = false,
  options: object,
) {
  const [isSubscribed, setIsSubscribed] = useState(initial)
  const [safeHandle] = sanitizeUrlParams(handle)
  const url = isSubscribed
    ? `${routes.UNSUBSCRIBE}/${safeHandle}`
    : `${routes.SUBSCRIBE}/${safeHandle}`
  const { mutateAsync } = useMutativeRequest(url, options)
  return [mutateAsync, setIsSubscribed]
}

/**
 * Upload Profile Photo
 */
export function useSetProfileLogo() {
  return useMutativeRequest(routes.PROFILE_LOGO)
}

/**
 * Create user profile
 */
export function useProfileCreation() {
  return useMutativeRequest(routes.PROFILES_API)
}

/**
 * Gets user profile information
 */
export function useProfileDetails(options = { refetchOnWindowFocus: true }) {
  return useMakeRequest<ProfileDetailsResponse, SellerMgmtServErrorResponse>(
    routes.PROFILES_API,
    options,
  )
}

/**
 * Edit user profile
 */
export function useEditProfile() {
  return useMutativeRequest(routes.PROFILES_API, { method: 'PATCH' })
}

/**
 * Validate user profile
 */
export function useProfileValidation() {
  return useMutativeRequest<unknown, SellerMgmtServErrorResponse>(
    routes.VALIDATE_PROFILES_API,
  )
}

/**
 * Get business handle suggestions if the given handle is not available
 */
export function useBusinessHandleSuggestions() {
  return useMutativeRequest(routes.VALIDATE_BUSINESS_HANDLE_API)
}
